import type { Garden, Plant } from "./types";

// Demo = everything lives in memory only; a reload or leaving the page forgets it.
let demo = false;
export const isDemo = () => demo;
export function startDemoMode() { demo = true; }

export const DEMO_USER = { id: "demo", email: "ukázka" };

/** Fictional plot drawn over an orchard (not a real cadastral parcel). */
export function demoGarden(): Garden {
  const id = "00000000-0000-4000-8000-00000000d3e0";
  return {
    id, name: "Ukázková zahrada",
    mode: "map",
    parcels: [{
      id: "00000000-0000-4000-8000-00000000d3e1", garden_id: id, ruian_id: 0, ku_kod: 0, ku_nazev: "ukázka",
      cislo: "ukázková parcela", druh_pozemku: 6, vymera: 4500,
      geometry: { type: "Polygon", coordinates: [[[15.5813648, 50.3748867], [15.5822541, 50.3748996], [15.5822744, 50.3742551], [15.581385, 50.3742422], [15.5813648, 50.3748867]]] },
    }],
  };
}

export function demoPlants(gardenId: string): Plant[] {
  const now = new Date().toISOString();
  const rows: [number, number, string, string, number | null, string, Plant["stav"], string][] = [
    [50.37479, 15.5815265, "Jabloň", "Jonagold", 2019, "M9", "zdravy", "Opylovač: Topaz"],
    [50.37479, 15.5817084, "Jabloň", "Topaz", 2019, "M9", "zdravy", ""],
    [50.3747836, 15.5818903, "Hrušeň", "Konference", 2020, "kdouloň MA", "slabsi", "Slabší přírůstky, přihnojit"],
    [50.3745967, 15.5815265, "Třešeň", "Kordia", 2018, "Gisela 5", "zdravy", ""],
    [50.3745902, 15.5817084, "Slivoň", "Čačanská lepotica", 2021, "myrobalán", "zdravy", ""],
    [50.3744355, 15.5820722, "Meruňka", "Bergeron", null, "", "planovany", "Výsadba na jaře"],
  ];
  return rows.map(([lat, lng, druh, odruda, rok, podnoz, stav, poznamka], i) => ({
    id: "00000000-0000-4000-8000-0000000d3" + String(100 + i).padStart(3, "0"),
    garden_id: gardenId, cislo: i + 1, druh, odruda, rok, podnoz, stav, poznamka, lat, lng,
    parcela: "ukázková parcela", urceni: null, updated_at: now,
  }));
}

/** Generates a top-down visual image of a flowerbed / berry bed (1200x800) for the linked demo subgarden. */
async function generateFlowerbedBlob(): Promise<{ blob: Blob; width: number; height: number }> {
  const width = 1200;
  const height = 800;
  const c = document.createElement("canvas");
  c.width = width;
  c.height = height;
  const g = c.getContext("2d")!;

  // 1. Grass background around the bed
  const grassGrad = g.createLinearGradient(0, 0, width, height);
  grassGrad.addColorStop(0, "#4c7c38");
  grassGrad.addColorStop(0.5, "#55883f");
  grassGrad.addColorStop(1, "#447031");
  g.fillStyle = grassGrad;
  g.fillRect(0, 0, width, height);

  // Subtle grass blades / speckles
  for (let i = 0; i < 1800; i++) {
    const x = (i * 97) % width;
    const y = (i * 53 + ((i * 13) % 71)) % height;
    g.fillStyle = i % 2 === 0 ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)";
    g.fillRect(x, y, 3, 3);
  }

  // 2. Gravel walkway around & through the middle of two raised beds
  g.fillStyle = "#c9c2b2";
  g.beginPath();
  g.roundRect(55, 55, width - 110, height - 110, 24);
  g.fill();

  // Gravel texture dots
  for (let i = 0; i < 2200; i++) {
    const x = 60 + ((i * 73) % (width - 120));
    const y = 60 + ((i * 41 + Math.floor(i / 17) * 19) % (height - 120));
    g.fillStyle = i % 3 === 0 ? "#b2aa98" : i % 3 === 1 ? "#ded8ca" : "#9c9484";
    g.fillRect(x, y, 3, 3);
  }

  // Helper to draw a wooden raised bed with soil, mulch, and drip irrigation lines
  const drawBed = (bx: number, by: number, bw: number, bh: number, label: string) => {
    // Shadow
    g.fillStyle = "rgba(0,0,0,0.28)";
    g.beginPath();
    g.roundRect(bx + 6, by + 8, bw, bh, 12);
    g.fill();

    // Wooden frame (outer)
    g.fillStyle = "#8b5a2b";
    g.beginPath();
    g.roundRect(bx, by, bw, bh, 10);
    g.fill();
    g.strokeStyle = "#5c3816";
    g.lineWidth = 3;
    g.stroke();

    // Inner soil & bark mulch
    const pad = 18;
    const sx = bx + pad, sy = by + pad, sw = bw - pad * 2, sh = bh - pad * 2;
    const soilGrad = g.createLinearGradient(sx, sy, sx + sw, sy + sh);
    soilGrad.addColorStop(0, "#473121");
    soilGrad.addColorStop(0.5, "#3d291a");
    soilGrad.addColorStop(1, "#4e3624");
    g.fillStyle = soilGrad;
    g.fillRect(sx, sy, sw, sh);

    // Mulch chips texture
    for (let i = 0; i < 900; i++) {
      const mx = sx + 6 + ((i * 61) % (sw - 12));
      const my = sy + 6 + ((i * 37 + Math.floor(i / 11) * 23) % (sh - 12));
      g.fillStyle = i % 3 === 0 ? "#5c3f28" : i % 3 === 1 ? "#2f1e12" : "#6b4930";
      g.fillRect(mx, my, 5, 3);
    }

    // Drip irrigation lines along the bed
    g.strokeStyle = "rgba(30, 30, 30, 0.55)";
    g.lineWidth = 3;
    g.beginPath();
    g.moveTo(sx + 10, sy + sh * 0.35);
    g.lineTo(sx + sw - 10, sy + sh * 0.35);
    g.moveTo(sx + 10, sy + sh * 0.68);
    g.lineTo(sx + sw - 10, sy + sh * 0.68);
    g.stroke();

    // Subtle wooden corner posts
    g.fillStyle = "#6e451f";
    for (const [cx, cy] of [[bx, by], [bx + bw - pad, by], [bx, by + bh - pad], [bx + bw - pad, by + bh - pad]]) {
      g.fillRect(cx, cy, pad, pad);
      g.strokeRect(cx, cy, pad, pad);
    }

    // Bed label on the wooden rim
    g.fillStyle = "rgba(255,248,235,0.85)";
    g.font = "bold 14px sans-serif";
    g.fillText(label, bx + 26, by + 14);
  };

  // Upper raised bed (Kanadské borůvky a zimolez – kyselá sekce)
  drawBed(90, 85, 1020, 275, "SEKCE A – BORŮVKY A ZIMOLEZ (RAŠELINA)");
  // Lower raised bed (Rybíz, angrešt a maliny)
  drawBed(90, 440, 1020, 275, "SEKCE B – RYBÍZ, ANGREŠT A MALINY");

  // Stepping stones in the central gravel path
  for (let x = 180; x <= 1020; x += 168) {
    g.fillStyle = "#8d877c";
    g.beginPath();
    g.ellipse(x, 400, 46, 22, 0, 0, Math.PI * 2);
    g.fill();
    g.strokeStyle = "#6f6a60";
    g.lineWidth = 2;
    g.stroke();
  }

  // Draw planting spots (green foliage for existing plants, dashed soil rings for planned plants)
  // Coordinate mapping: x = (lng / 80) * 1200, y = 800 - (lat / 80) * 1200
  const spots: { lat: number; lng: number; planned: boolean; radius: number }[] = [
    { lat: 41.5, lng: 14.0, planned: false, radius: 52 },
    { lat: 41.5, lng: 31.0, planned: true, radius: 52 },
    { lat: 41.5, lng: 49.0, planned: true, radius: 48 },
    { lat: 41.5, lng: 66.0, planned: true, radius: 48 },
    { lat: 19.0, lng: 14.0, planned: false, radius: 48 },
    { lat: 19.0, lng: 31.0, planned: true, radius: 44 },
    { lat: 14.5, lng: 49.0, planned: true, radius: 38 },
    { lat: 14.5, lng: 61.0, planned: true, radius: 38 },
    { lat: 14.5, lng: 71.0, planned: true, radius: 38 },
  ];

  for (const s of spots) {
    const cx = (s.lng / 80) * width;
    const cy = height - (s.lat / 80) * width;
    if (s.planned) {
      // Prepared planting pit / mulch ring for planned plant
      g.fillStyle = "rgba(25, 16, 9, 0.55)";
      g.beginPath();
      g.arc(cx, cy, s.radius * 0.75, 0, Math.PI * 2);
      g.fill();

      g.save();
      g.strokeStyle = "rgba(255, 210, 63, 0.75)";
      g.lineWidth = 3;
      g.setLineDash([8, 6]);
      g.beginPath();
      g.arc(cx, cy, s.radius, 0, Math.PI * 2);
      g.stroke();
      g.restore();
    } else {
      // Existing green shrub canopy
      g.fillStyle = "rgba(0,0,0,0.28)";
      g.beginPath();
      g.arc(cx + 5, cy + 6, s.radius, 0, Math.PI * 2);
      g.fill();

      const leafGrad = g.createRadialGradient(cx - 10, cy - 10, 6, cx, cy, s.radius);
      leafGrad.addColorStop(0, "#7ec850");
      leafGrad.addColorStop(0.7, "#4b8b2b");
      leafGrad.addColorStop(1, "#2f6118");
      g.fillStyle = leafGrad;
      g.beginPath();
      g.arc(cx, cy, s.radius, 0, Math.PI * 2);
      g.fill();
    }
  }

  const blob = await new Promise<Blob>((resolve, reject) =>
    c.toBlob((b) => (b ? resolve(b) : reject(new Error("canvas toBlob"))), "image/jpeg", 0.86),
  );
  return { blob, width, height };
}

export async function demoSubgarden(parentId: string): Promise<Garden> {
  const id = "00000000-0000-4000-8000-00000000d3e2";
  const { blob, width, height } = await generateFlowerbedBlob();
  return {
    id,
    name: "Záhonek drobného ovoce",
    mode: "photo",
    photo_width: width,
    photo_height: height,
    photo_blob: blob,
    parent_id: parentId,
    parent_lat: 50.37435,
    parent_lng: 15.58165,
    parcels: [],
  };
}

export function demoSubgardenPlants(subgardenId: string): Plant[] {
  const now = new Date().toISOString();
  const rows: [number, number, string, string, number | null, string, Plant["stav"], string][] = [
    [41.5, 14.0, "Borůvka", "Patriot", 2024, "vlastní kořeny", "zdravy", "Už vysazená v kyselé rašelině"],
    [41.5, 31.0, "Borůvka", "Bluecrop", null, "vlastní kořeny", "planovany", "V plánu k dosazení na jaře (opylovač k Patriot)"],
    [41.5, 49.0, "Zimolez kamčatský", "Vostorg", null, "vlastní kořeny", "planovany", "Plánovaná výsadba – raná odrůda"],
    [41.5, 66.0, "Zimolez kamčatský", "Aurora", null, "vlastní kořeny", "planovany", "Plánovaná výsadba – vzájemný opylovač k Vostorg"],
    [19.0, 14.0, "Rybíz", "Titania", 2023, "vlastní kořeny", "zdravy", "Černý rybíz, silný růst"],
    [19.0, 31.0, "Angrešt", "Invicta", null, "meruzalka", "planovany", "Plánovaný stromkový angrešt odolný padlí"],
    [14.5, 49.0, "Malina", "Polka", null, "vlastní kořeny", "planovany", "Plánovaná řada remontantních malin (1. kus)"],
    [14.5, 61.0, "Malina", "Polka", null, "vlastní kořeny", "planovany", "Plánovaná řada malin (2. kus, spon 60 cm)"],
    [14.5, 71.0, "Malina", "Polka", null, "vlastní kořeny", "planovany", "Plánovaná řada malin (3. kus, spon 60 cm)"],
  ];
  return rows.map(([lat, lng, druh, odruda, rok, podnoz, stav, poznamka], i) => ({
    id: "00000000-0000-4000-8000-0000000d3" + String(200 + i).padStart(3, "0"),
    garden_id: subgardenId,
    cislo: i + 1,
    druh,
    odruda,
    rok,
    podnoz,
    stav,
    poznamka,
    lat,
    lng,
    parcela: "",
    urceni: null,
    updated_at: now,
  }));
}
